/**
 * @vuelto-surface
 * mobile: ok
 * desktop: ok
 * strategy: unified
 */
import React from 'react';
import { StyleSheet, Switch, Text, TouchableOpacity, View } from 'react-native';
import { spacing } from '../../../shared/theme/spacing'
import { ticketColors, ticketFonts } from '../../../shared/theme/ticketColors';
import { PaperSheet } from '../../../shared/ticket/PaperSheet';
import { settingsStyles as styles } from '../settingsScreen.styles';
import {
  DAY_LABELS,
  type BusinessHourDay,
  type StoreProfileDraft,
  type TimeShift,
} from '../../domain/storeProfileTypes';
import { LocalTimeField } from './LocalDateTimeField';

type Props = {
  draft: StoreProfileDraft;
  canEdit: boolean;
  saving: boolean;
  /** Tabla compacta en desktop (4 cols como el proto). */
  table?: boolean;
  onBusinessHourChange: (dayOfWeek: number, patch: Partial<BusinessHourDay>) => void;
  onAddShift: (dayOfWeek: number) => void;
  onRemoveShift: (dayOfWeek: number, index: number) => void;
  onShiftChange: (dayOfWeek: number, index: number, field: keyof TimeShift, value: string) => void;
};

function TimeInline({
  open,
  close,
  canEdit,
  saving,
  showRemove,
  onOpenChange,
  onCloseChange,
  onRemove,
}: {
  open: string;
  close: string;
  canEdit: boolean;
  saving: boolean;
  showRemove: boolean;
  onOpenChange: (v: string) => void;
  onCloseChange: (v: string) => void;
  onRemove: () => void;
}) {
  const locked = !canEdit || saving;
  return (
    <View style={local.timeInline}>
      <LocalTimeField
        value={open}
        onChange={onOpenChange}
        disabled={locked}
        accessibilityLabel="Hora de apertura"
      />
      <Text style={local.timeSep}>a</Text>
      <LocalTimeField
        value={close}
        onChange={onCloseChange}
        disabled={locked}
        accessibilityLabel="Hora de cierre"
      />
      {canEdit && showRemove ? (
        <TouchableOpacity
          onPress={onRemove}
          disabled={saving}
          hitSlop={8}
          accessibilityRole="button"
          accessibilityLabel="Quitar turno"
          style={local.removeBtn}
        >
          <Text style={local.removeText}>×</Text>
        </TouchableOpacity>
      ) : null}
    </View>
  );
}

export function StoreHoursEditor({
  draft,
  canEdit,
  saving,
  table,
  onBusinessHourChange,
  onAddShift,
  onRemoveShift,
  onShiftChange,
}: Props) {
  if (table) {
    return (
      <PaperSheet padded>
        <Text style={styles.cardTitle}>Horarios</Text>
        <Text style={local.caption}>Hora local del dispositivo del cliente.</Text>
        <View style={local.table}>
          <View style={local.tableHead}>
            <Text style={[local.th, local.colDay]}>Día</Text>
            <Text style={[local.th, local.colOpen]}>Abierto</Text>
            <Text style={[local.th, local.colShifts]}>Turnos</Text>
            <View style={local.colAdd} />
          </View>
          {draft.businessHours.map((day, index) => {
            const canAdd = canEdit && day.isOpen && day.shifts.length < 3;
            return (
              <View
                key={day.dayOfWeek}
                style={[local.tableRow, index === draft.businessHours.length - 1 && local.tableRowLast]}
              >
                <Text style={[local.dayText, local.colDay]}>{DAY_LABELS[day.dayOfWeek]}</Text>
                <View style={local.colOpen}>
                  <Switch
                    value={day.isOpen}
                    onValueChange={(isOpen) => onBusinessHourChange(day.dayOfWeek, { isOpen })}
                    disabled={!canEdit || saving}
                    trackColor={{ false: ticketColors.rule, true: ticketColors.gold }}
                    thumbColor={ticketColors.ink}
                  />
                </View>
                <View style={local.colShifts}>
                  {day.isOpen ? (
                    day.shifts.map((shift, si) => (
                      <TimeInline
                        key={si}
                        open={shift.open}
                        close={shift.close}
                        canEdit={canEdit}
                        saving={saving}
                        showRemove={day.shifts.length > 1}
                        onOpenChange={(v) => onShiftChange(day.dayOfWeek, si, 'open', v)}
                        onCloseChange={(v) => onShiftChange(day.dayOfWeek, si, 'close', v)}
                        onRemove={() => onRemoveShift(day.dayOfWeek, si)}
                      />
                    ))
                  ) : (
                    <Text style={local.closedText}>Cerrado</Text>
                  )}
                </View>
                <View style={local.colAdd}>
                  {canAdd ? (
                    <TouchableOpacity
                      onPress={() => onAddShift(day.dayOfWeek)}
                      disabled={saving}
                      accessibilityRole="button"
                      accessibilityLabel="Agregar turno"
                      hitSlop={6}
                    >
                      <Text style={local.addShiftText}>+ Turno</Text>
                    </TouchableOpacity>
                  ) : null}
                </View>
              </View>
            );
          })}
        </View>
      </PaperSheet>
    );
  }

  return (
    <PaperSheet padded>
      <Text style={styles.cardTitle}>Horarios</Text>
      <Text style={styles.captionBlock}>Según la hora del celular de quien pide.</Text>
      <View style={{ gap: spacing.sm }}>
        {draft.businessHours.map((day) => {
          const canAdd = canEdit && day.isOpen && day.shifts.length < 3;
          return (
            <View key={day.dayOfWeek} style={local.hoursDayCard}>
              <View style={local.hoursDayHead}>
                <Text style={local.dayText}>{DAY_LABELS[day.dayOfWeek]}</Text>
                <Switch
                  value={day.isOpen}
                  onValueChange={(isOpen) => onBusinessHourChange(day.dayOfWeek, { isOpen })}
                  disabled={!canEdit || saving}
                  trackColor={{ false: ticketColors.rule, true: ticketColors.gold }}
                  thumbColor={ticketColors.ink}
                />
              </View>
              {day.isOpen ? (
                <View style={local.mobileShifts}>
                  {day.shifts.map((shift, si) => (
                    <TimeInline
                      key={si}
                      open={shift.open}
                      close={shift.close}
                      canEdit={canEdit}
                      saving={saving}
                      showRemove={day.shifts.length > 1}
                      onOpenChange={(v) => onShiftChange(day.dayOfWeek, si, 'open', v)}
                      onCloseChange={(v) => onShiftChange(day.dayOfWeek, si, 'close', v)}
                      onRemove={() => onRemoveShift(day.dayOfWeek, si)}
                    />
                  ))}
                  {canAdd ? (
                    <TouchableOpacity
                      onPress={() => onAddShift(day.dayOfWeek)}
                      disabled={saving}
                      accessibilityRole="button"
                      accessibilityLabel="Agregar turno"
                    >
                      <Text style={local.addShiftText}>+ Agregar turno</Text>
                    </TouchableOpacity>
                  ) : null}
                </View>
              ) : (
                <Text style={local.closedText}>Cerrado</Text>
              )}
            </View>
          );
        })}
      </View>
    </PaperSheet>
  );
}

const local = StyleSheet.create({
  tableCard: {
    gap: 4,
    alignSelf: 'stretch',
  },
  caption: {
    fontSize: 12,
    color: ticketColors.ink2,
    fontFamily: ticketFonts.sans,
    lineHeight: 17,
    marginBottom: 4,
  },
  table: {
    backgroundColor: ticketColors.paper,
    borderWidth: 1,
    borderColor: ticketColors.ruleSoft,
    borderRadius: 14,
    overflow: 'hidden',
  },
  tableHead: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: ticketColors.paper2,
    paddingHorizontal: 12,
    paddingVertical: 10,
    borderBottomWidth: 1,
    borderBottomColor: ticketColors.ruleSoft,
  },
  th: {
    fontSize: 11,
    fontWeight: '700',
    color: ticketColors.ink3,
    textTransform: 'uppercase',
    letterSpacing: 0.3,
    fontFamily: ticketFonts.sans,
  },
  tableRow: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 12,
    paddingVertical: 10,
    borderBottomWidth: 1,
    borderBottomColor: ticketColors.ruleSoft,
  },
  tableRowLast: {
    borderBottomWidth: 0,
  },
  colDay: {
    width: 110,
    flexShrink: 0,
  },
  colOpen: {
    width: 72,
    flexShrink: 0,
    alignItems: 'flex-start',
    justifyContent: 'center',
  },
  /** Crece: los inputs quedan a la izquierda; + Turno vive en colAdd al final. */
  colShifts: {
    flex: 1,
    minWidth: 0,
    gap: 6,
    justifyContent: 'center',
  },
  /** Última columna del proto (th vacío). */
  colAdd: {
    width: 72,
    flexShrink: 0,
    alignItems: 'flex-end',
    justifyContent: 'center',
    paddingLeft: 8,
  },
  dayText: {
    fontSize: 13,
    fontWeight: '600',
    color: ticketColors.ink,
    fontFamily: ticketFonts.sans,
  },
  closedText: {
    fontSize: 13,
    color: ticketColors.ink2,
    fontFamily: ticketFonts.sans,
  },
  timeInline: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
  },
  timeSep: {
    fontSize: 12,
    color: ticketColors.ink2,
    fontFamily: ticketFonts.sans,
    flexShrink: 0,
  },
  removeBtn: {
    paddingHorizontal: 4,
    minHeight: 32,
    justifyContent: 'center',
    flexShrink: 0,
  },
  removeText: {
    fontSize: 16,
    lineHeight: 18,
    color: ticketColors.ink2,
    fontFamily: ticketFonts.sans,
  },
  addShiftText: {
    fontSize: 12,
    fontWeight: '600',
    fontFamily: ticketFonts.sans,
    color: ticketColors.gold,
    paddingVertical: 4,
  },
  hoursDayCard: {
    borderWidth: 1,
    borderColor: ticketColors.ruleSoft,
    borderRadius: 10,
    paddingVertical: 10,
    paddingHorizontal: 12,
    gap: 8,
  },
  hoursDayHead: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: spacing.sm,
  },
  mobileShifts: {
    gap: 8,
    alignItems: 'flex-start',
  },
});
